// components/sections/SkillDataEditor.js
import { X } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

export const SkillDataEditor = ({ skillName, data, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: skillName || '', // Nouveau champ pour le nom
    description: data?.description || '',
    training_link: data?.training_link || '',
    certification: data?.certification || '',
    niveau_maitrise_requis: data?.niveau_maitrise_requis || 'Débutant'
  });
  const [hasChanges, setHasChanges] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    const originalData = {
      name: skillName || '',
      description: data?.description || '',
      training_link: data?.training_link || '',
      certification: data?.certification || '',
      niveau_maitrise_requis: data?.niveau_maitrise_requis || 'Débutant'
    };
    
    const hasChanged = Object.keys(formData).some(key => formData[key] !== originalData[key]);
    setHasChanges(hasChanged);
  }, [formData, data, skillName]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        if (hasChanges) {
          if (window.confirm('Vous avez des modifications non sauvegardées. Voulez-vous vraiment fermer sans sauvegarder ?')) {
            onCancel();
          }
        } else {
          onCancel();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [hasChanges, onCancel]);

  const handleSave = () => {
    // Vérifier que le nom n'est pas vide
    if (!formData.name.trim()) {
      alert('Le nom de la compétence ne peut pas être vide');
      return;
    }
    
    onSave(skillName, formData); // skillName = ancien nom, formData.name = nouveau nom
    onCancel();
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm('Vous avez des modifications non sauvegardées. Voulez-vous vraiment fermer sans sauvegarder ?')) {
        onCancel();
      }
    } else {
      onCancel();
    }
  };

  const handleNiveauChange = (niveau) => {
    setFormData({ ...formData, niveau_maitrise_requis: niveau });
  };

  const niveaux = ['Débutant', 'Intermédiaire', 'Avancé', 'Expert'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div 
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-lg">
          <h4 className="text-lg font-semibold text-gray-900">
            Détails de la compétence
          </h4>
          <button 
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Nom de la compétence */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom de la compétence
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Nom de la compétence"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#18574F] focus:border-[#18574F]"
              required
            />
          </div>

          {/* Niveau de difficulté */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Difficulté
            </label>
            <div className="flex gap-2">
              {niveaux.map((niveau) => (
                <button
                  key={niveau}
                  onClick={() => handleNiveauChange(niveau)}
                  className={`flex-1 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                    formData.niveau_maitrise_requis === niveau
                      ? 'bg-[#18574F] text-white border-[#18574F]'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {niveau}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description de la compétence..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#18574F] focus:border-[#18574F] resize-none"
            />
          </div>

          {/* Lien de formation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lien de formation
            </label>
            <input
              type="url"
              value={formData.training_link}
              onChange={(e) => setFormData({ ...formData, training_link: e.target.value })}
              placeholder="https://example.com/formation"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#18574F] focus:border-[#18574F]"
            />
          </div>

          {/* Certification */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Certification
            </label>
            <input
              type="text"
              value={formData.certification}
              onChange={(e) => setFormData({ ...formData, certification: e.target.value })}
              placeholder="Nom de la certification"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#18574F] focus:border-[#18574F]"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end space-x-3 rounded-b-lg">
          <button 
            onClick={handleCancel} 
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button 
            onClick={handleSave} 
            className="px-4 py-2 text-sm font-medium bg-[#18574F] text-white rounded-md hover:bg-[#2D7A6E] transition-colors"
          >
            Sauvegarder
          </button>
        </div>
      </div>
    </div>
  );
};